/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ListBox } from 'primereact/listbox';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { useRef } from 'react';
import { getApiKey } from '../../../api/apiKey/ApiKey';
import { authenticationAPI } from '../../../api/authentication/AuthenticateAPI';
import {
  countries,
  jobs,
  uses,
} from '../../../assets/config/informations/informationReference';
import {
  Item,
  MyGeneralInformation,
  MyProfileInformation,
} from '../../../models/MyInformation';
import './ItemsInformation.scss';

/**
 * Items Information Props type.
 */
export interface ItemsInformationProps {
  isUserGDH?: boolean;
  informationIcon: string;
  informationTitle: string;
  informationBody: MyGeneralInformation | MyProfileInformation | {};
  setMyGeneralInformation: Function;
  setMyProfileInformation: Function;
  handleEditProfile: Function;
}

/**
 * Items Information Container displays cards for each information.
 * @param informationIcon
 * @param informationTitle
 * @param informationBody
 * @returns Element.
 */
export const ItemsInformation = ({
  isUserGDH,
  informationIcon,
  informationTitle,
  informationBody,
  setMyGeneralInformation,
  setMyProfileInformation,
  handleEditProfile,
}: ItemsInformationProps) => {
  /**
   * API service to get API KEY from Keycloak
   * @return response
   */
  const { data, refetch } = useQuery({
    queryKey: ['GetApiKey'],
    queryFn: getApiKey,
    enabled: false,
    refetchOnWindowFocus: false,
  });
  const op = useRef<any>(null);
  /**
   * Call the API KEY Query
   */
  const fetchApiKey = () => {
    void refetch();
  };

  /**
   * Manage API KEY view inside the card
   * @return Element
   */
  const manageApiKeyComponent = () => {
    if (!data) {
      return (
        <Button
          aria-label="generate-apikey"
          size="small"
          label="Generate"
          severity="warning"
          className="myInformation-api-key-btn"
          onClick={() => fetchApiKey()}
        />
      );
    } else {
      return (
        <div className="grid">
          <div
            className="col"
            style={{
              padding: 4,
            }}
          >
            <InputTextarea
              id="api-key"
              style={{
                border: 0,
                padding: 0,
                color: '#002D80',
              }}
              value={data.value}
              rows={1}
              cols={17}
              autoResize
            />
          </div>
          <Tooltip target=".icon-copy-apikey" />
          <span
            aria-label="copy-apikey"
            className="col icon-copy-apikey"
            data-pr-tooltip="Copy"
            data-pr-position="bottom"
            onClick={() => {
              void navigator.clipboard.writeText(data.value);
            }}
          />
          <Tooltip target=".icon-refresh-apikey" />
          <span
            aria-label="refresh-apikey"
            className="col icon-refresh-apikey"
            data-pr-tooltip="Regenerate"
            data-pr-position="bottom"
            onClick={() => fetchApiKey()}
          />
        </div>
      );
    }
  };

  /**
   * Renders an editable field based on whether the field is part of the profile or general information.
   * If the field is editable, it will render either an input field or a dropdown, depending on the field type.
   *
   * @param element - The name of the field to be rendered.
   * @param isProfile - A boolean indicating whether the field belongs to the profile section.
   * @returns A JSX element representing the editable field.
   */
  const renderEditableField = (element: string, isProfile: boolean) => {
    const setInformation = isProfile
      ? setMyProfileInformation
      : setMyGeneralInformation;
    const information: Item =
      informationBody[element as keyof typeof informationBody];
    const isEditable = information?.isEditable;
    const value = information?.value;

    if (!isEditable) {
      return (
        <div
          className={classNames('col myInformation-card-value', {
            email: element === 'mail',
          })}
        >
          {value}
        </div>
      );
    }

    /**
     * Handles value change by updating the corresponding state and triggering profile update.
     *
     * @param newValue - The new value to be updated.
     */
    const handleChange = (newValue: any) => {
      setInformation({
        ...informationBody,
        [element]: { value: newValue, isEditable: false },
      });
      setTimeout(handleEditProfile, 1000);
    };

    if (['use', 'job', 'country'].includes(element)) {
      const options =
        element === 'use' ? uses : element === 'job' ? jobs : countries;
      return (
        <Dropdown
          value={value}
          onChange={(e) => handleChange(e.value.name)}
          options={options}
          optionLabel="name"
          placeholder={`Select a ${element}`}
          className="w-full md:w-14rem"
        />
      );
    }

    return (
      <InputText
        aria-label="inputtext"
        className="p-inputtext-sm"
        name={element}
        value={value as string}
        onChange={(e) =>
          setInformation({
            ...informationBody,
            [element]: { value: e.target.value, isEditable: true },
          })
        }
        onBlur={() => handleChange(value)}
        onKeyPress={(e) => e.key === 'Enter' && handleChange(value)}
      />
    );
  };

  /**
   * Renders an edit icon that toggles the edit mode of a field when clicked.
   *
   * @param element - The field name to be edited.
   * @param isProfile - Boolean indicating if the field belongs to the profile section.
   * @returns A JSX element representing the edit icon or null if editing is not allowed.
   */
  const renderEditIcon = (element: string, isProfile: boolean) => {
    if (['mail', 'groups'].includes(element) || !isUserGDH) return null;

    const setInformation = isProfile
      ? setMyProfileInformation
      : setMyGeneralInformation;
    return (
      <div
        aria-label="icon-edit"
        className="icon-edit"
        onClick={() =>
          setInformation({
            ...informationBody,
            [element]: {
              ...(informationBody[
                element as keyof typeof informationBody
              ] as Item),
              isEditable: !(
                informationBody[element as keyof typeof informationBody] as Item
              )?.isEditable,
            },
          })
        }
      />
    );
  };

  /**
   * Renders a list box displaying the user's groups.
   *
   * @param element - The field name for which the list box is rendered.
   * @returns A JSX element containing a button to toggle the group list overlay.
   */
  const renderListBox = (element: string) => (
    <>
      <div className="col myInformation-card-value"></div>
      <Button
        text
        aria-label="viewUserGroups"
        icon="pi pi-eye"
        onClick={(e) => op.current.toggle(e)}
        tooltip="View user groups"
        tooltipOptions={{ position: 'right' }}
        style={{ color: '#002d80', marginRight: '-1em' }}
        className="EditGroupButton"
      />
      <OverlayPanel ref={op}>
        <ListBox
          filter
          options={
            (
              informationBody[element as keyof typeof informationBody] as {
                value: string[];
                isEditable: boolean;
              }
            )?.value || []
          }
          listStyle={{ maxHeight: '250px' }}
          emptyMessage="No groups found"
          emptyFilterMessage="Group not found"
        />
      </OverlayPanel>
    </>
  );

  /**
   * Renders the appropriate body content based on the card type.
   *
   * @param cardInfoType - The type of card (e.g., "General", "Profile information", "Authentication").
   * @returns A JSX element containing the appropriate fields and components for the card type.
   */
  const bodyInformation = (cardInfoType: string) => {
    if (
      ['General', 'Profile information', 'Accessibility'].includes(cardInfoType)
    ) {
      return Object.keys(informationBody || {}).map((element, index) => (
        <div className="grid myInformation-card-body" key={element + index}>
          <div
            className={classNames('col myInformation-card-key', {
              email: element === 'mail',
            })}
          >
            {element}
          </div>
          {element === 'groups' ? (
            renderListBox(element)
          ) : (
            <>
              {renderEditableField(
                element,
                cardInfoType === 'Profile information'
              )}
              {cardInfoType !== 'Accessibility' && element !== 'mail' && (
                <div className="editProfile">
                  {renderEditIcon(
                    element,
                    cardInfoType === 'Profile information'
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ));
    }

    if (cardInfoType === 'Authentication') {
      return (
        <>
          <div className="grid myInformation-card-body">
            <ConfirmDialog />
            <div className="col myInformation-card-key">Password</div>
            <div className="col password-edit-dots">...............</div>
            <Tooltip target=".icon-edit" />
            <div
              aria-label="icon-edit"
              className="icon-edit"
              data-pr-tooltip="Change password"
              data-pr-position="bottom"
              onClick={() =>
                authenticationAPI.login({ action: 'UPDATE_PASSWORD' })
              }
            />
          </div>
          <div className="grid myInformation-card-body">
            <div className="col myInformation-card-key">API Key</div>
            <div className="col">{manageApiKeyComponent()}</div>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="information-card">
      <div className="grid p-card-header">
        <div
          className="icon-information-card"
          style={{
            background: `url(${informationIcon}) 50% 50% no-repeat`,
          }}
        />
        <div className="title">{informationTitle}</div>
      </div>
      <Divider type="solid" className="divider" />
      <div className="p-card-body">{bodyInformation(informationTitle)}</div>
    </div>
  );
};
